import { signal, type Signal } from '@preact/signals-react';
import type { AppParams, Scope } from '../types';

/**
 * Payload the home grid attaches when launching a feature, e.g.
 *   { scope: 'module', initialMessage?: string, ... }
 *
 * Lives at module level so:
 *   • the WidgetShell can read it to render the active feature, AND
 *   • the API mutation factory can read the scope to scope-shape the
 *     /activity/* request body, without each feature view having to
 *     thread the scope through props/hooks.
 */
export type FeaturePayload = { scope?: Scope } & Record<string, unknown>;

export const featurePayloadSignal: Signal<FeaturePayload | null> = signal(null);

/**
 * Convenience reader. Defaults to 'course' so features launched without a
 * scope (e.g. the chat composer in HomeView) still resolve to a sensible
 * backend context.
 */
export function getCurrentScope(): Scope {
  const value = featurePayloadSignal.value;
  return value?.scope ?? 'course';
}

/**
 * Resolve the active module id. Hosts can pass `module_id` explicitly OR
 * we derive it from the last segment of `route` when that segment looks
 * like an HTM identifier — the LMS route is always shaped
 *   /AFO.../MOD.../UDI.../HTM...
 * and the trailing HTM is the resource the user is currently on.
 * If the last segment is something else (e.g. an EVA evaluation page),
 * we return null — that's not a content module, so module-scope tools
 * shouldn't be shown.
 */
export function getEffectiveModuleId(params: AppParams): string | null {
  if (params.module_id) return params.module_id;
  const route = params.route;
  if (!route) return null;
  const segments = route.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  return last?.startsWith('HTM') ? last : null;
}

/**
 * Pick the narrowest scope whose IDs are all present in `params`. Used by
 * features that should always send the richest context possible regardless
 * of where they were launched from — chat is the canonical case: the user
 * expects Phia to know about the exact module they're on, not just "the
 * course". Mirrors the visibility rules in HomeView.tsx.
 */
export function pickBestScope(params: AppParams): Scope {
  const onActivityModule = params.module_kind === 'activity';
  if (params.MOD && params.UDI && getEffectiveModuleId(params) && !onActivityModule) {
    return 'module';
  }
  if (params.MOD && params.UDI) return 'section';
  return 'course';
}
